/*
  ml-trial-preselect — adds the 7-day trial to a new visitor's empty cart, once per browser, so
  member prices show from the first product they add. Loaded only when
  snippets/ml-trial-preselect.liquid allows the page (setting on, not a member, not an excluded
  page). Case matrix and risks: docs/CART-MEMBERSHIP-DEFAULT.md.

  localStorage.ml_trial_preselect records the one decision per browser:
    pending:<ts> | added | failed | skipped:<reason> | removed:<how>
  Any value means "never again", so a trial the visitor removed (or a "No membership" pick) is
  never re-added, and two tabs can't both add.

  Rules:
  - not a bot (navigator.webdriver, crawler/preview/headless user agents), and only after the
    visitor's first scroll, pointer or key press on an interactive page;
  - no earlier membership signal in this browser (a cart route pick, the 30-day offer);
  - the cart must be empty; otherwise this browser is marked skipped and never touched;
  - one /cart/add.js call, run through MlCartV2's queue when it is on the page, with the line
    property _ml_trial_source=auto (orders and PostHog can tell it from a chosen trial). The
    qty guard keeps one membership line, so a membership chosen later replaces it;
  - a product added with the buy box's guest route ("Buy without membership") removes the auto
    trial first, then the add goes ahead;
  - a checkout link or form outside the cart surfaces opens the drawer first while the visitor
    hasn't yet seen the trial notice, so no one reaches checkout with a subscription unseen.
  Analytics: ml_trial_autoadd {result}, ml_trial_autoadd_skipped {reason},
  ml_trial_autoadd_removed {how}. No "membership chosen" event is sent for the auto add.
*/
(function () {
  'use strict';
  if (window.MlTrialPreselectRun) return;
  window.MlTrialPreselectRun = true;
  var C = window.MlTrialPreselect;
  if (!C || !C.variant || !C.plan) return;

  var KEY = 'ml_trial_preselect';
  var SEEN = 'ml_trial_preselect_seen';
  var BOT = /bot\b|bot\/|crawl|spider|slurp|mediapartners|adsbot|headless|lighthouse|pagespeed|gtmetrix|pingdom|uptime|monitor|preview|prerender|facebookexternalhit|facebot|embedly|quora link|whatsapp|telegram|slack|discord|skype|bingpreview|google-inspectiontool|googleother|python|curl|wget|axios|node-fetch|go-http|java\/|okhttp|phantom|selenium|puppeteer|playwright|scrapy|ahrefs|semrush|mj12|dotbot|petalbot|yandex|baidu|bytespider|gptbot|claudebot|perplexity|ccbot/i;

  function get(k) { try { return window.localStorage.getItem(k); } catch (e) { return 'nostorage'; } }
  function set(k, v) { try { window.localStorage.setItem(k, v); } catch (e) {} }
  function track(name, props) {
    try { if (window.posthog && window.posthog.capture) window.posthog.capture(name, props || {}); } catch (e) {}
  }
  function json(url, body) {
    return fetch(url, body ? {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    } : { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (d) { if (!r.ok) throw d; return d; });
    });
  }
  function autoLines(cart) {
    return (cart.items || []).filter(function (i) { return i.properties && i.properties._ml_trial_source === 'auto'; });
  }
  function isBot() {
    return !!navigator.webdriver || BOT.test(navigator.userAgent || '') || !('localStorage' in window);
  }
  function earlierMembershipSignal() {
    return !!(get('ml_cart_last_route') || get('ml_exit30_taken') || get('ml_exit30_declined'));
  }

  function refreshCart() {
    if (window.MonthleesCartDrawer && window.MonthleesCartDrawer.refresh) return window.MonthleesCartDrawer.refresh();
    if (window.MlCartV2 && window.MlCartV2.render) return window.MlCartV2.render();
    return Promise.resolve();
  }

  // A guest-route add made while the decision is still pending wins over the pre-select.
  var guestDuring = false;

  function removeAuto(how) {
    return json('/cart.js').then(function (cart) {
      var lines = autoLines(cart);
      if (!lines.length) return null;
      var updates = {};
      lines.forEach(function (l) { updates[l.key] = 0; });
      return json('/cart/update.js', { updates: updates }).then(function (after) {
        set(KEY, 'removed:' + how);
        track('ml_trial_autoadd_removed', { how: how });
        return after;
      });
    });
  }

  function attempt() {
    if (get(KEY)) return;
    if (isBot()) return;
    if (guestDuring) { set(KEY, 'skipped:guest_route'); return; }
    if (earlierMembershipSignal()) { set(KEY, 'skipped:history'); track('ml_trial_autoadd_skipped', { reason: 'history' }); return; }
    set(KEY, 'pending:' + Date.now());
    json('/cart.js').then(function (cart) {
      if (cart.item_count > 0) { set(KEY, 'skipped:cart'); track('ml_trial_autoadd_skipped', { reason: 'cart_not_empty' }); return null; }
      var add = function () {
        return json('/cart/add.js', { items: [{ id: C.variant, quantity: 1, selling_plan: C.plan, properties: { _ml_trial_source: 'auto' } }] });
      };
      var run = window.MlCartV2 && window.MlCartV2.mutate
        ? window.MlCartV2.mutate(add)
        : add().then(function () {
          document.dispatchEvent(new CustomEvent('monthlees:cart-updated', { detail: { source: 'ml-trial-preselect' } }));
        });
      return Promise.resolve(run).then(function () { return json('/cart.js'); }).then(function (after) {
        var ok = autoLines(after).length > 0;
        set(KEY, ok ? 'added' : 'failed');
        track('ml_trial_autoadd', { result: ok ? 'added' : 'failed' });
        if (ok && guestDuring) return removeAuto('guest_route').then(refreshCart);
        if (ok && window.MlCartV2 && window.MlCartV2.updateBadges) window.MlCartV2.updateBadges(after.item_count);
      });
    }).catch(function () { set(KEY, 'failed'); track('ml_trial_autoadd', { result: 'failed' }); });
  }

  // First interaction on an interactive page, then one attempt.
  var armed = false;
  function arm() {
    if (armed || get(KEY)) return;
    armed = true;
    ['pointerdown', 'keydown', 'scroll', 'touchstart'].forEach(function (t) {
      window.addEventListener(t, once, { passive: true, capture: true });
    });
  }
  function once() {
    ['pointerdown', 'keydown', 'scroll', 'touchstart'].forEach(function (t) {
      window.removeEventListener(t, once, { capture: true });
    });
    window.setTimeout(attempt, 400);
  }
  if (document.readyState !== 'loading') arm();
  else document.addEventListener('DOMContentLoaded', arm);

  // A guest-route add ("Buy without membership") takes the auto trial out first.
  var bypass = false;
  document.addEventListener('submit', function (e) {
    if (bypass) return;
    var sub = e.submitter;
    if (!sub || !sub.matches || !sub.matches('[data-monthlees-add-guest]')) return;
    var state = get(KEY) || '';
    if (state !== 'added') { if (!state || state.indexOf('pending') === 0) guestDuring = true; return; }
    var form = e.target;
    e.preventDefault();
    e.stopImmediatePropagation();
    removeAuto('guest_route').catch(function () {}).then(function () {
      bypass = true;
      try { if (form.requestSubmit) form.requestSubmit(sub); else form.submit(); } finally { bypass = false; }
    });
  }, true);

  // The visitor has seen the notice once it is on screen (drawer open or cart page).
  function markSeen() {
    var n = document.querySelector('[data-mlcg-presel]');
    if (n && n.getClientRects().length) set(SEEN, '1');
  }
  document.addEventListener('ml-cart-v2:rendered', function () { window.setTimeout(markSeen, 50); });
  var d = document.querySelector('[data-ml-cart-drawer]');
  if (d && 'MutationObserver' in window) {
    new MutationObserver(function () { if (d.classList.contains('is-open')) window.setTimeout(markSeen, 300); })
      .observe(d, { attributes: true, attributeFilter: ['class'] });
  }

  // Record a removal made in the cart, for the analytics only (a removed trial is never re-added).
  document.addEventListener('ml-cart-v2:rendered', function () {
    if (get(KEY) !== 'added') return;
    json('/cart.js').then(function (cart) {
      if (!autoLines(cart).length) { set(KEY, 'removed:cart'); track('ml_trial_autoadd_removed', { how: 'cart' }); }
    }).catch(function () {});
  });

  // No checkout with an unseen subscription: a checkout link or form outside the cart surfaces
  // opens the drawer first while the notice hasn't been seen.
  function unseen() { return get(KEY) === 'added' && !get(SEEN); }
  function outsideCart(el) { return !(el.closest && el.closest('[data-ml-cart-root]')); }
  function showCart(e) {
    e.preventDefault();
    e.stopImmediatePropagation();
    if (window.MonthleesCartDrawer && window.MonthleesCartDrawer.open) window.MonthleesCartDrawer.open();
    else window.location.href = '/cart';
  }
  document.addEventListener('click', function (e) {
    if (!unseen()) return;
    var a = e.target && e.target.closest && e.target.closest('a[href]');
    if (!a || !outsideCart(a)) return;
    var path = '';
    try { path = new URL(a.href, window.location.href).pathname; } catch (err) { return; }
    if (/^\/checkouts?(\/|$)/.test(path)) showCart(e);
  }, true);
  document.addEventListener('submit', function (e) {
    if (!unseen()) return;
    var form = e.target;
    var sub = e.submitter;
    var toCheckout = (sub && sub.name === 'checkout') || /\/checkout/.test(form.getAttribute('action') || '');
    if (toCheckout && outsideCart(sub || form)) showCart(e);
  }, true);
})();
